<template>
  <div class="flex h-screen overflow-hidden bg-gray-100">
    <!-- Off-canvas menu for mobile, show/hide based on off-canvas menu state. -->
    <mobile-menu
      :sidebar-open="mobileMenuOpen"
      @close="mobileMenuOpen = false"
    />

    <sidebar class="print:!hidden" />

    <div
      class="flex-1 overflow-auto focus:outline-none"
      tabindex="0"
    >
      <div
        class="relative z-10 flex flex-shrink-0 h-16 bg-white border-b border-gray-200 lg:border-none print:hidden"
      >
        <button
          class="px-4 text-gray-400 border-r border-gray-200 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-teal-500 lg:hidden print:hidden"
          @click="mobileMenuOpen = !mobileMenuOpen"
        >
          <span class="sr-only">Open sidebar</span>
          <!-- Heroicon name: outline/menu-alt-1 -->
          <ld-icon
            class="w-4 h-4"
            icon="MenuAlt1Icon"
          />
        </button>
        <!-- Search bar -->
        <div
          v-if="authStore.organizations.length > 0"
          class="text-gray-400 text-sm flex items-center justify-center px-2 print:hidden"
        >
          Organization:
          <span class="text-gray-900 ml-2 text-md font-bold">{{
            authStore.selectedOrganization?.attributes.name
          }}</span>
          <router-link
            v-if="organization?.attributes.isPrepaid && $can('list', 'UserLicenseToken')"
            class="rounded bg-teal-600 px-2 py-1 ml-4 text-xs font-semibold text-white shadow-sm hover:bg-teal-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
            :to="{ name: 'UserLicenses' }"
          >
            <span v-if="organization?.attributes?.allIncluded">Manage Licenses</span>
            <span v-else-if="organization?.attributes?.callsIncluded">Manage Licenses
              {{ formatNumber(organization?.attributes?.creditsInCents / 100) }}</span>
            <span v-else>{{ formatNumber(organization?.attributes?.creditsInCents / 100) }}</span>
          </router-link>
        </div>
        <div class="flex justify-between flex-1 px-4 sm:px-6 lg:max-w-6xl lg:mx-auto lg:px-8">
          <call-banner
            v-if="callStore.call"
            class="hidden md:inline-flex"
          />
          <incoming-call
            v-if="!!callStore.incomingCall"
            class="hidden md:inline-flex"
          />
          <IncomingTransfer
            v-if="!!callStore.incomingTransfer"
            class="hidden md:inline-flex"
          />
          <div class="flex flex-1 text-xs" />
          <div class="flex items-center ml-4 md:ml-6">
            <phone-status />
            <user-menu />
          </div>
        </div>
      </div>
      <main class="relative z-0 flex-1 pb-8 overflow-y-auto h-full">
        <call-banner
          v-if="callStore.call"
          class="md:hidden"
        />
        <incoming-call
          v-if="!!callStore.incomingCall"
          class="md:hidden"
        />
        <AlertsVue />
        <NotificationVue />
        <IncomingTransfer
          v-if="!!callStore.incomingTransfer"
          class="md:hidden"
        />
        <Suspense>
          <router-view />
        </Suspense>
      </main>
    </div>
  </div>
  <div class="absolute bottom-0 right-0 text-gray-600 px-2 print:hidden">
    v{{ appVersion }}
  </div>
  <CallErrorModal />
  <DispositionModal />
  <OrganizationDispositionModal />
  <OrganizationSelectModal v-if="!selectedOrganization" />
  <audio id="audio-remote" />
</template>

<script setup lang="ts">
  import { computed, onMounted, onUnmounted, Ref, ref, watch } from 'vue'
  import Sidebar from '../components/layout/SideMenu/Sidebar.vue'
  import MobileMenu from '../components/layout/SideMenu/MobileMenu.vue'
  import { useAuthStore } from '../store/authStore'
  import { Permission } from '@letsdial/api/src/lib/types/User'
  import { useAbility } from '@casl/vue'
  import { Ability, AbilityBuilder } from '@casl/ability'

  import UserMenu from '../components/layout/UserMenu.vue'
  import PhoneStatus from '../components/layout/PhoneStatus.vue'
  import useLetsDialSocket from '../services/letsDialSocket/useLetsDialSocket'
  import useLetsDialPhone from '../services/letsDialPhone/useLetsDialPhone'
  import CallBanner from '/@/components/layout/CallBanner.vue'
  import CallErrorModal from '/@/components/ui/modals/CallErrorModal.vue'
  import DispositionModal from '/@/components/ui/modals/DispositionModal.vue'
  import OrganizationDispositionModal from '/@/components/ui/modals/OrganizationDispositionModal.vue'
  import { useCallStore } from '../store/callStore'
  import { useRecordStore } from '../store/recordStore'
  import IncomingCall from '../components/layout/IncomingCall.vue'
  import IncomingTransfer from '../components/layout/IncomingTransfer.vue'
  import OrganizationSelectModal from '../components/ui/modals/OrganizationSelectModal.vue'
  import { Howl } from 'howler'
  import LeadPop from '/@/assets/sounds/LeadPOP.ogg?url'
  import { PopRecordEvent, SocketEventTypes } from '@letsdial/socket'
  import useLetsDialAPI from '../services/letsDialAPI/useLetsDialAPI'
  import useApiGlobalSEE from '../services/letsDialAPI/useApiGlobalSEE'
  import NotificationVue from '../components/ui/Notification.vue'
  import { useLetsDialExtensions } from '../services/useLetsDialExtensions'
  import { useInitStore } from '../store/initStore'
  import { Organization } from '@letsdial/api/src'
  import AlertsVue from '../components/ui/Alerts.vue'

  const initStore = useInitStore()
  const authStore = useAuthStore()
  const callStore = useCallStore()
  const recordStore = useRecordStore()
  const ability = useAbility()
  const { socket, listen: socketListen } = useLetsDialSocket()
  const { phone, listen } = useLetsDialPhone()
  const api = useLetsDialAPI()
  const sse = useApiGlobalSEE()
  const extensionManager = useLetsDialExtensions()
  const mobileMenuOpen = ref(false)
  const organization: Ref<Organization | undefined> = ref(undefined)

  const onCallStatus = ({ status }) => {
    if (status === 'ANSWERED') {
      authStore.setAvailabilityStatus('ON_CALL')
    }
  }

  const onPopRecord = async (record: PopRecordEvent) => {
    recordStore.setPop(record)
    const beepVolume = authStore.user?.attributes?.additionalAttributes?.beepVolume ?? 0.05
    const sound = new Howl({ src: [LeadPop], volume: beepVolume })
    authStore.setAvailabilityStatus('ON_LEAD')
    sound.play()
    console.log(record)
    if (record.callID) {
      const call = await api.calls.get(record.callID)
      if (call) {
        callStore.setCall(call.data ?? undefined)
      }
    }
  }

  const onConferenceJoining = ({ roomId }) => {
    console.log(`JOINING EVENT ${roomId}`)
    callStore.setConferenced(true)
    callStore.setRoomId(roomId)
    socket.emit(SocketEventTypes.ConferenceJoin, 1, roomId)
  }

  const onConferenceLeaving = () => {
    console.log(`JOINING Leaving`)
    socket.emit(SocketEventTypes.ConferenceLeave, 1, callStore.conferenceRoomId)
    callStore.setConferenced(false)
    callStore.setRoomId(undefined)
  }

  listen('call:status', onCallStatus)
  socketListen('pop:record', onPopRecord)
  listen('conference:joining', onConferenceJoining)
  listen('conference:leaving', onConferenceLeaving)

  onMounted(async () => {
    initStore.mount()
    sse.mount()
    extensionManager.mount()

    setInterval(async () => {
      initStore.initialize()
      if (authStore.selectedOrganization?.attributes.isPrepaid && authStore.selectedOrganization.id) {
        organization.value =
          (await api.organizations.get(authStore.selectedOrganization.id)).data ?? undefined
        authStore.updateSelectedOrganizationCredits(
          organization.value?.attributes.creditsInCents ?? 0,
        )
      }
    }, 60000)

    window.onbeforeunload = function (e) {
      if (authStore.currentStatus === 'IDLE' || authStore.currentStatus === 'NO_PHONE') {
        return undefined
      }
      return false
    }

    if (authStore.permissions.length > 0) {
      const { can, rules } = new AbilityBuilder(Ability)
      authStore.permissions.forEach((permission) => {
        can(permission.action, permission.subject)
      })
      ability.update(rules)
    }
  })

  onUnmounted(() => {
    initStore.unmount()
    sse.unMount()
    extensionManager.unMount()
  })

  watch(
    () => authStore.permissions,
    (permissions: Array<Permission>) => {
      const { can, rules } = new AbilityBuilder(Ability)
      permissions.forEach((permission) => {
        can(permission.action, permission.subject)
      })
      ability.update(rules)
    },
  )

  watch(
    () => authStore.selectedOrganization,
    async (val) => {
      if (val?.attributes.isPrepaid && val.id) {
        organization.value = (await api.organizations.get(val.id)).data ?? undefined
        authStore.updateSelectedOrganizationCredits(
          organization.value?.attributes.creditsInCents ?? 0,
        )
      }
    },
    { immediate: true },
  )

  watch(
    () => authStore.extensionPassword,
    (password) => {
      if (password) {
        initStore.initialize()
      }
    },
  )

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(num)
  }

  const selectedOrganization = computed(() => authStore.selectedOrganization)
  const appVersion = __APP_VERSION__
</script>
